import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchRoles, addRole, updateRole, deleteRole,
  clearSuccess, clearError
} from '../redux/slices/roleSlice';
import '../assets/RoleMaster.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faTag, faPaperPlane, faEdit, faTrash, faCheckCircle, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

const RoleMaster = () => {
  const dispatch = useDispatch();
  const { items: roles, status, success, error } = useSelector((state) => state.roles);

  const [formData, setFormData] = useState({ roleName: '', rolePrefix: '' });
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rolesPerPage = 10;

  useEffect(() => { dispatch(fetchRoles()); }, [dispatch]);

  useEffect(() => {
    if (success) { const timer = setTimeout(() => dispatch(clearSuccess()), 3000); return () => clearTimeout(timer); }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) { const timer = setTimeout(() => dispatch(clearError()), 3000); return () => clearTimeout(timer); }
  }, [error, dispatch]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearError());
    const rolePayload = {
      superadminId: localStorage.getItem("adminuserId"),
      name: formData.roleName.trim(),
      prefix: formData.rolePrefix.trim(),
    };
    if (editId) {
      dispatch(updateRole({ id: editId, data: rolePayload })).then(() => {
        dispatch(fetchRoles());
        setEditId(null);
      });
    } else {
      dispatch(addRole(rolePayload)).then(() => dispatch(fetchRoles()));
    }
    setFormData({ roleName: '', rolePrefix: '' });
  };

  const handleEdit = (role) => {
    setFormData({ roleName: role.name, rolePrefix: role.prefix });
    setEditId(role.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      dispatch(deleteRole(id)).then(() => dispatch(fetchRoles()));
    }
  };

  const totalRoles = roles.length;
  const totalPages = Math.ceil(totalRoles / rolesPerPage);
  useEffect(() => { if (currentPage > totalPages && totalPages > 0) setCurrentPage(totalPages); }, [totalPages, currentPage]);
  const paginatedRoles = roles.slice((currentPage - 1) * rolesPerPage, currentPage * rolesPerPage);

  return (
    <div className="RoleManagement-container">
      {/* ====== Header Card ====== */}
      <section className="RoleManagement-section header-card top-box-rolemanagement">
        <h2 className="RoleManagement-heading">{editId ? 'Edit Role' : 'Create New Role'}</h2>
        {success && (
          <div className="RoleManagement-success">
            <FontAwesomeIcon icon={faCheckCircle} /> {editId ? 'Role Updated Successfully!' : 'New Role Added Successfully!'}
          </div>
        )}
        {error && (
          <div className="RoleManagement-error">
            <FontAwesomeIcon icon={faExclamationCircle} /> {error}
          </div>
        )}

        {/* ====== Role Form ====== */}
        <form className="RoleManagement-form" onSubmit={handleSubmit}>
          <div className="RoleManagement-field-top">
            <label htmlFor="roleName"  >Role Name</label>
            <div className="RoleManagement-inputWrapper">
              <span className="RoleManagement-icon"><FontAwesomeIcon icon={faUser} /></span>
              <input
                id="roleName"
                type="text"
                placeholder="Enter Role Name"
                value={formData.roleName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="RoleManagement-field-top">
            <label htmlFor="rolePrefix">Role Prefix</label>
            <div className="RoleManagement-inputWrapper">
              <span className="RoleManagement-icon"><FontAwesomeIcon icon={faTag} /></span>
              <input
                id="rolePrefix"
                type="text"
                placeholder="Enter Role Prefix"
                value={formData.rolePrefix}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="RoleManagement-buttonWrapper">
            <button type="submit" className="RoleManagement-submitBtn" disabled={status === 'loading'}>
              <FontAwesomeIcon icon={faPaperPlane} /> {editId ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </section>

      {/* ====== Roles Table ====== */}
      <section className="RoleManagement-section table-card">
        <h3 className="RoleManagement-heading">Role Management</h3>
        {status === 'loading' && roles.length === 0 ? (
          <p className="loading-text">Loading roles...</p>
        ) : (
          <div className="RoleManagement-tableWrapper">
            <div className="RoleManagement-scrollWrapper">
              <table className="RoleManagement-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Role Name</th>
                    <th>Prefix</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRoles.map((role, i) => (
                    <tr key={role.id}>
                      <td>{(currentPage - 1) * rolesPerPage + i + 1}</td>
                      <td>{role.name||"N/A"}</td>
                      <td>{role.prefix||"N/A"}</td>
                      <td>{new Date(role.createdAt).toLocaleDateString("en-GB")||"N/A"}</td>
                      <td>{new Date(role.updatedAt).toLocaleDateString("en-GB")||"N/A"}</td>
                      <td className="action-cell">
                        <button className="RoleManagement-actionBtn edit" onClick={() => handleEdit(role)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="RoleManagement-actionBtn delete" onClick={() => handleDelete(role.id)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ====== Pagination ====== */}
            <div className="RoleManagement-pagination">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={i + 1 === currentPage ? 'active' : ''}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default RoleMaster;
